import React, { useCallback, useState } from 'react'
import styles from '../../../CSS/main.css'
import { ListParams, ParamsUpdater } from '../List/hooks'
import Button, { ButtonActive } from '../Button/Button'
import { Filter } from './types'
import { Collapsible } from './Collapsible'
import { RadioFilter } from './RadioFilter'
import { CheckBoxFilter } from './CheckBoxFilter'
import { DateRangeFilter } from './DateRangeFilter'

const FilterRenderer = <T,>(
  filter: Filter<T>,
  updateParams: ParamsUpdater<T>,
  filterState: T
): JSX.Element => {
  // eslint-disable-next-line functional/no-conditional-statement
  switch (filter.type) {
    case 'daterange':
      return (
        <DateRangeFilter
          filter={filter}
          key={filter.displayName}
          start={filterState[filter.startFieldName]}
          end={filterState[filter.endFieldName]}
          updateParams={updateParams}
        />
      )
    case 'checkbox':
      return (
        <CheckBoxFilter
          key={filter.displayName}
          filter={filter}
          value={
            filterState[filter.fieldName] as unknown as ReadonlyArray<
              string | number
              >
          }
          updateParams={updateParams}
        />
      )
    case 'radio':
      return (
        <RadioFilter
          key={filter.displayName}
          filter={filter}
          value={filterState[filter.fieldName]}
          updateParams={updateParams}
        />
      )
  }
}

type Props<T extends ListParams> = {
  readonly filters: ReadonlyArray<Filter<T>>
  readonly filterState: T
  readonly updateParams: (params: T) => void
  readonly onClose: () => void
  readonly onReset: () => T
}

export const Filters = <T extends ListParams>({
                                                filters,
                                                updateParams,
                                                filterState,
                                                onClose,
                                                onReset,
                                              }: Props<T>): JSX.Element => {
  const [localFilterState, setLocalFilterState] = useState<T>(filterState)
  const updateLocal = useCallback<ParamsUpdater<T>>((key, value) => {
    setLocalFilterState((prev) => ({ ...prev, [key]: value }))
  }, [])

  const onResetClick = useCallback(() => {
    setLocalFilterState(onReset())
    onClose()
  }, [localFilterState])

  const onApplySearch = useCallback(() => {
    updateParams(localFilterState)
    onClose()
  }, [localFilterState, updateParams])

  return (
    <>
      {filters.map((f) => (
        <Collapsible key={f.displayName} title={f.displayName} isDateRangePicker={f.type === 'daterange'}>
          {FilterRenderer(f, updateLocal, localFilterState)}
        </Collapsible>
      ))}

      <div className={styles.ButtonBlock}>
        <ButtonActive onClick={onApplySearch}>Search</ButtonActive>
        <Button onClick={onResetClick}>Reset</Button>
      </div>
    </>
  )
}
